// Edit or delete ONE discovery answer cell.
//
// LIFTED from the two apps' views/ConferenceDetail/ResponseEditModal.tsx, which
// were near-identical copies differing only in a type-import path and whether
// `companyId` was required. Keeping two copies meant a fix to one silently
// missed the other; this is the `feature-authoring.md` "build it once in the
// shared library" rule. Endpoint access arrives as injected closures — shared
// code must contain no endpoint literals.
import { useEffect, useState } from 'react';
import { Button, Input, Modal, Select, Space, Typography, message } from 'antd';
import type { DiscoveryQuestion } from './types';

export interface DiscoveryAnswerBody {
  selectedOptionIds?: string[];
  freeText?: string | null;
}

export interface ResponseEditModalProps {
  open: boolean;
  question: DiscoveryQuestion;
  answer: { selectedOptionIds: string[]; freeText: string | null } | undefined; // current answer (may be empty)
  leadName?: string;
  onSave: (body: DiscoveryAnswerBody) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose: () => void;
  onSaved: () => void; // caller refetches
}

/**
 * CALLER CONTRACT: mount this with a React `key` unique to the cell being
 * edited — e.g. `` `${personId}:${question.id}` `` — whenever the same
 * component instance could be reused for two different cells.
 *
 * Draft state is seeded once per mount from `answer`/`question`, and the
 * reset effect below keys off `question.id` and `answer` alone (there is no
 * `personId` prop to disambiguate). Two different UNANSWERED cells for the
 * SAME question both have `answer === undefined` — referentially equal — so
 * if a caller reuses one mounted instance across cells instead of keying it,
 * the effect will not re-run and the second cell will silently show the
 * first cell's blank draft. A `key` forces React to unmount/remount instead
 * of reusing the instance, which is what actually distinguishes the cells.
 */
export function ResponseEditModal({
  open,
  question,
  answer,
  leadName,
  onSave,
  onDelete,
  onClose,
  onSaved,
}: ResponseEditModalProps) {
  const isFreeText = question.type === 'free_text';
  const [freeText, setFreeText] = useState<string>(answer?.freeText ?? '');
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(
    answer?.selectedOptionIds ?? [],
  );
  const [saving, setSaving] = useState(false);

  // Reset local state whenever the targeted cell (or open state) changes.
  useEffect(() => {
    setFreeText(answer?.freeText ?? '');
    setSelectedOptionIds(answer?.selectedOptionIds ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, question.id, answer]);

  const handleSave = async () => {
    const body: DiscoveryAnswerBody = isFreeText
      ? { freeText }
      : {
          selectedOptionIds,
          ...(question.hasOtherOption ? { freeText } : {}),
        };
    setSaving(true);
    try {
      await onSave(body);
      message.success('Response updated');
      onSaved();
      onClose();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!onDelete) return;
    Modal.confirm({
      title: 'Delete this response?',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await onDelete();
          message.success('Response deleted');
          onSaved();
          onClose();
        } catch (err) {
          message.error((err as { message?: string }).message ?? 'Delete failed');
        }
      },
    });
  };

  const options = question.options.map((o) => ({ label: o.text, value: o.id }));

  return (
    <Modal
      open={open}
      title={leadName ? `Edit response — ${leadName}` : 'Edit response'}
      onCancel={onClose}
      destroyOnClose
      footer={[
        ...(onDelete
          ? [
              <Button key="delete" danger onClick={handleDelete} style={{ float: 'left' }}>
                Delete response
              </Button>,
            ]
          : []),
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Typography.Text type="secondary">{question.text}</Typography.Text>

        {isFreeText ? (
          <Input.TextArea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            autoSize={{ minRows: 3 }}
            aria-label="Response text"
          />
        ) : (
          <>
            <Select
              mode={question.allowMultiple ? 'multiple' : undefined}
              options={options}
              value={
                question.allowMultiple
                  ? selectedOptionIds
                  : (selectedOptionIds[0] ?? undefined)
              }
              onChange={(val: string | string[]) =>
                setSelectedOptionIds(Array.isArray(val) ? val : val ? [val] : [])
              }
              allowClear
              style={{ width: '100%' }}
              placeholder="Select an option"
              aria-label="Response options"
            />
            {question.hasOtherOption ? (
              <Input.TextArea
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                autoSize={{ minRows: 2 }}
                placeholder="Other…"
                aria-label="Other response text"
              />
            ) : null}
          </>
        )}
      </Space>
    </Modal>
  );
}
